import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { InnerBlock } from "@/components/InnerBlock";

// Categories set by this site today. Where a specific vendor cookie name
// is used we list it; where we describe a family of cookies set by a
// third-party service, we describe the family rather than invent names.
const COOKIE_ROWS = [
  ["wafa.cookie-consent.v1", "Strictly necessary", "Records your cookie choice so we do not ask again on every page.", "12 months"],
  ["wafa.lang.v1 (legacy)", "Strictly necessary", "Held your language preference before the /ar/ URL prefix took over. Kept only for existing visitors.", "12 months"],
  ["wafa.chat.session.v1", "Strictly necessary", "Keeps your live-chat conversation attached to you across page reloads.", "Session"],
  ["GA4 analytics cookies (set only after consent)", "Analytics", "Google Analytics 4 uses first-party cookies (typically _ga and _ga_<container-id>) to measure page views and returning visitors. Loaded only if you accept analytics cookies.", "13 months"],
];

export default function CookiePolicy() {
  return (
    <div data-testid="cookie-policy-page">
      <Seo title="Cookie Policy" description="How Wafa Solutions LLC uses cookies and what choices you have." />
      <PageHero
        eyebrow="Legal"
        h1="Cookie Policy"
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Cookie Policy" }]}
      />

      <InnerBlock tint="white">
        <article className="mx-auto max-w-3xl space-y-10 text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">What cookies are</h2>
            <p className="mt-4">
              Cookies are small text files a website stores on your device. They let a site remember things — your language choice, whether you are logged in, how you arrived — and let us understand how the site is used.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">How we use them</h2>
            <ul className="mt-4 space-y-3">
              <li><strong>Strictly necessary cookies</strong> make the site work: security, form submission, and remembering your cookie choices. The site cannot function without them, so they do not require your consent.</li>
              <li><strong>Analytics cookies</strong> tell us which pages are read, how people arrive, and where they leave; these only run if you accept them.</li>
              <li><strong>Marketing cookies</strong> let us measure our advertising; these only run if you accept them.</li>
            </ul>
            <p className="mt-4">
              We do not use cookies to build a personal profile of you, and we do not sell cookie data.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">Cookies we set</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-sm">
                <thead>
                  <tr className="bg-wafa-cream/60 text-start text-wafa-deep">
                    <th className="border border-wafa-deep/10 p-3 font-semibold">Cookie</th>
                    <th className="border border-wafa-deep/10 p-3 font-semibold">Type</th>
                    <th className="border border-wafa-deep/10 p-3 font-semibold">Purpose</th>
                    <th className="border border-wafa-deep/10 p-3 font-semibold">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {COOKIE_ROWS.map((r, i) => (
                    <tr key={i} className="align-top">
                      <td className="border border-wafa-deep/10 p-3">{r[0]}</td>
                      <td className="border border-wafa-deep/10 p-3">{r[1]}</td>
                      <td className="border border-wafa-deep/10 p-3">{r[2]}</td>
                      <td className="border border-wafa-deep/10 p-3">{r[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">Your choices</h2>
            <p className="mt-4">
              When you first visit, we ask before setting anything beyond strictly necessary cookies. You can change your mind at any time through the <button
                type="button"
                data-testid="cookie-policy-settings-link"
                className="underline underline-offset-2 hover:text-wafa-deep"
                onClick={() => window.dispatchEvent(new CustomEvent("wafa:open-cookie-settings"))}
              >Cookie Settings</button> link in our footer, or by clearing cookies in your browser. Blocking necessary cookies may stop parts of the site working.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">Third parties</h2>
            <p className="mt-4">
              Some cookies are set by services we use, listed above. Those providers have their own policies, and where they process data outside Oman we handle that as described in section 5 of our <a href="/privacy-policy" className="underline underline-offset-2">Privacy Policy</a>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">Contact</h2>
            <p className="mt-4"><a href="mailto:support@wafa.om" className="underline underline-offset-2">support@wafa.om</a></p>
          </section>
        </article>
      </InnerBlock>
    </div>
  );
}
